import { dispatchCartUpdated } from "./pent-core/events.js"
import { sectionRenderer, normalizeSectionId } from "./pent-core/section-renderer.js"

class ATCGroup extends HTMLElement {
  #abortController
  #cartUpdateHandler

  connectedCallback() {
    // Listen for submit on the component itself (delegation)
    this.addEventListener("submit", this.#handleSubmit.bind(this))

    // Listen for cart updates to re-render the section
    this.#cartUpdateHandler = this.#onCartUpdated.bind(this)
    document.addEventListener("cart:updated", this.#cartUpdateHandler)
  }

  disconnectedCallback() {
    document.removeEventListener("cart:updated", this.#cartUpdateHandler)
  }

  async #onCartUpdated(event) {
    const sectionWrapper = this.closest(".shopify-section")
    if (sectionWrapper?.id) {
      try {
        const sectionId = normalizeSectionId(sectionWrapper.id)
        await sectionRenderer.renderSection(sectionId, { cache: false })
      } catch (error) {
        console.error("Failed to update ATC group section:", error)
      }
    }
  }

  async #handleSubmit(event) {
    const form = event.target

    // Verify this is our form submission
    if (!form.matches("form[data-type='add-to-cart-form']")) return

    const behavior = this.dataset.afterAdd || "drawer"

    if (behavior === "cart") {
      return
    }

    event.preventDefault()

    if (this.hasAttribute("disabled")) {
      return
    }

    if (this.#abortController) {
      this.#abortController.abort()
    }
    this.#abortController = new AbortController()

    const formData = new FormData(form)

    try {
      this.setAttribute("busy", "")

      const url = (window.Shopify?.routes?.root || "/") + "cart/add.js"
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        signal: this.#abortController.signal,
        headers: {
          Accept: "application/json",
        },
      })

      if (!response.ok) {
        const error = await response.json()
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            detail: {
              message: error.description || error.message || "Failed to add to cart",
            },
            bubbles: true,
          })
        )
        throw new Error(error.description)
      }

      const addedItem = await response.json()

      const cartResponse = await fetch((window.Shopify?.routes?.root || "/") + "cart.js", {
        headers: { Accept: "application/json" },
      })
      const cart = await cartResponse.json()

      dispatchCartUpdated(document, { cart, addedItem })
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Add to cart failed:", error)
      }
    } finally {
      this.removeAttribute("busy")
      this.#abortController = undefined
    }
  }
}

if (!customElements.get("pent-atc-group")) {
  customElements.define("pent-atc-group", ATCGroup)
}
